import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { tr } from '../til';


const inputStyle = {
  padding: '10px 12px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '14px'
};

const genBtnStyle = {
  padding: '10px 14px',
  background: '#eef1f7',
  color: '#1a1a2e',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '13px',
  whiteSpace: 'nowrap',
  cursor: 'pointer'
};

// Xodim lavozimi. Kunlik topshiriqlar shu maydonga qarab beriladi:
// apparat bitta ro'yxatni, inspektor va katta inspektor esa shahar-tuman
// ro'yxatini oladi.
const STAFF_TYPES = [
  { value: 'apparat', label: tr("Apparat xodimi") },
  { value: 'inspektor', label: tr("Shahar-tuman: Inspektor") },
  { value: 'katta_inspektor', label: tr("Shahar-tuman: Katta inspektor") }
];

const staffTypeLabel = (value) =>
  STAFF_TYPES.find(s => s.value === value)?.label || null;

// "probatsiya" — mahkumlar reyestriga kiradigan alohida rol. Bu rol
// boshqa sahifalarni ko'rmaydi, admin esa mahkumlarni ko'rmaydi.
const ROLES = [
  { value: 'user', label: tr("Xodim") },
  { value: 'admin', label: tr("Administrator") },
  { value: 'probatsiya', label: tr("Probatsiya xodimi") }
];

// Chalkashadigan belgilar (0/O, 1/l/I) ishlatilmaydi — parol xodimga
// og'zaki yoki qog'ozda uzatiladi.
const randomPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const values = new Uint32Array(10);
  crypto.getRandomValues(values);
  return Array.from(values, v => chars[v % chars.length]).join('');
};

// Joriy admin o'z rolini o'zgartirib/faolsizlantirib qo'ymasligi uchun
// tokendan o'z ID'sini olamiz.
const currentUserId = () => {
  try {
    return JSON.parse(atob(localStorage.getItem('token').split('.')[1])).id;
  } catch {
    return null;
  }
};

function Users() {
  const [users, setUsers] = useState([]);
  const [zones, setZones] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [fullName, setFullName] = useState('');
  const [badgeNumber, setBadgeNumber] = useState('');
  const [password, setPassword] = useState('');
  const [rank, setRank] = useState('');
  const [department, setDepartment] = useState('');
  const [role, setRole] = useState('user');
  const [staffType, setStaffType] = useState('');
  const [workZoneId, setWorkZoneId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  // Tahrirlash oynasi
  const [editing, setEditing] = useState(null);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  // Tahrirlashda server "avval mahkumlarini o'tkazing" desa — shu xodim.
  const [editKerak, setEditKerak] = useState(null);

  // Xodim almashtirish oynasi: ketayotgan xodimning mahkumlari va rejadagi
  // sayyor sudlari yangi inspektorga o'tadi, tarix saqlanadi.
  const [alm, setAlm] = useState(null);
  const [almError, setAlmError] = useState('');
  const [almSaving, setAlmSaving] = useState(false);
  const [tarix, setTarix] = useState(null);

  const token = localStorage.getItem('token');
  const myId = currentUserId();

  const loadUsers = useCallback(() => {
    axios.get(`${API}/users/all`, { headers: { authorization: token } })
      .then(res => setUsers(res.data));
  }, [token]);

  const loadZones = useCallback(() => {
    axios.get(`${API}/attendance/zones`, { headers: { authorization: token } })
      .then(res => setZones(res.data.filter(z => z.is_active)));
  }, [token]);

  // Tuman ovozli yig'ilish uchun kerak: rahbar tumanlarni ko'radi va
  // ularga so'z beradi. Tumansiz xodim yig'ilishga umuman kira olmaydi.
  const loadDistricts = useCallback(() => {
    axios.get(`${API}/districts`, { headers: { authorization: token } })
      .then(res => setDistricts(res.data.filter(d => d.is_active)))
      .catch(() => setDistricts([]));
  }, [token]);

  useEffect(() => {
    loadUsers();
    loadZones();
    loadDistricts();
  }, [loadUsers, loadZones, loadDistricts]);

  const createUser = async () => {
    setError('');
    setSuccess('');

    if (!fullName || !password) {
      setError(tr("F.I.Sh va parol majburiy"));
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/users/create`, {
        full_name: fullName,
        badge_number: badgeNumber,
        password,
        rank,
        department,
        role,
        staff_type: staffType || null,
        work_zone_id: workZoneId || null,
        district_id: districtId || null
      }, { headers: { authorization: token } });

      // Raqamni tizim bergan bo'lishi mumkin — shuning uchun javobdagisini
      // ko'rsatamiz, formaga yozilganini emas.
      setSuccess(
        tr("\"{fullName}\" qo'shildi. Kirish uchun: {badge_number} / {password}", { fullName, badge_number: res.data.user.badge_number, password }) +
        (badgeNumber ? '' : tr("  (loginni tizim berdi)"))
      );
      setFullName(''); setBadgeNumber(''); setPassword('');
      setRank(''); setDepartment(''); setRole('user'); setWorkZoneId('');
      setDistrictId(''); setStaffType('');
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || tr("Xodim qo'shilmadi. Qaytadan urinib ko'ring."));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (user) => {
    setEditError('');
    setSuccess('');
    setEditing({ ...user, newPassword: '' });
  };

  const saveEdit = async () => {
    setEditError('');
    setEditKerak(null);
    setEditSaving(true);
    try {
      const payload = {
        full_name: editing.full_name,
        badge_number: editing.badge_number,
        rank: editing.rank || '',
        department: editing.department || '',
        role: editing.role,
        staff_type: editing.staff_type || null,
        work_zone_id: editing.work_zone_id || null,
        district_id: editing.district_id || null
      };
      // Ball maydoni bo'sh qoldirilsa — tegmaymiz (aks holda jimgina 0 bo'lib qolardi).
      if (editing.score !== '' && editing.score !== null) {
        payload.score = Number(editing.score);
      }
      if (editing.newPassword) payload.password = editing.newPassword;

      await axios.put(`${API}/users/${editing.id}`, payload, {
        headers: { authorization: token }
      });

      setSuccess(
        editing.newPassword
          ? tr("\"{full_name}\" yangilandi. Yangi parol: {newPassword}", { full_name: editing.full_name, newPassword: editing.newPassword })
          : tr("\"{full_name}\" ma'lumotlari yangilandi.", { full_name: editing.full_name })
      );
      setEditing(null);
      loadUsers();
    } catch (err) {
      const d = err.response?.data;
      setEditError(d?.almashtirish_kerak
        ? `${d.error}: ${tr("{mahkum} ta mahkum, {sud} ta sayyor sud.", { mahkum: d.mahkum_soni, sud: d.sayyor_sud_soni })}`
        : d?.error || tr("Saqlanmadi. Qaytadan urinib ko'ring."));
      if (d?.almashtirish_kerak) setEditKerak(editing);
    } finally {
      setEditSaving(false);
    }
  };

  const openAlmashtirish = async (user, faolsizlantirish = false) => {
    setError('');
    setAlmError('');
    try {
      const res = await axios.get(`${API}/users/${user.id}/almashtirish`, {
        headers: { authorization: token }
      });
      setAlm({ ...res.data, yangiId: '', sabab: '', faolsizlantirish });
    } catch (err) {
      setError(err.response?.data?.error || tr("Amal bajarilmadi."));
    }
  };

  const saveAlmashtirish = async () => {
    setAlmError('');
    if (!alm.yangiId) { setAlmError(tr("Yangi inspektorni tanlang")); return; }
    if (alm.sabab.trim().length < 3) { setAlmError(tr("Almashtirish sababini yozing")); return; }
    if (alm.faolsizlantirish && !window.confirm(
      tr("\"{ism}\" faolsizlantiriladi va tizimga kira olmaydi. Davom etilsinmi?", { ism: alm.xodim.full_name })
    )) return;

    setAlmSaving(true);
    try {
      const res = await axios.post(`${API}/users/${alm.xodim.id}/almashtirish`, {
        yangi_xodim_id: Number(alm.yangiId),
        sabab: alm.sabab.trim(),
        faolsizlantirish: alm.faolsizlantirish
      }, { headers: { authorization: token } });
      const yangi = alm.nomzodlar.find(n => String(n.id) === String(alm.yangiId));
      setSuccess(tr("{eski} → {yangi}: {mahkum} ta mahkum va {sud} ta sayyor sud o'tkazildi.", {
        eski: alm.xodim.full_name,
        yangi: yangi?.full_name || '',
        mahkum: res.data.mahkum_soni,
        sud: res.data.sayyor_sud_soni
      }) + (res.data.faolsizlantirildi ? ' ' + tr("Xodim faolsizlantirildi.") : ''));
      setAlm(null);
      loadUsers();
      if (tarix) loadTarix();
    } catch (err) {
      setAlmError(err.response?.data?.error || tr("Amal bajarilmadi."));
    } finally {
      setAlmSaving(false);
    }
  };

  const loadTarix = () => {
    axios.get(`${API}/users/almashtirishlar`, { headers: { authorization: token } })
      .then(res => setTarix(res.data))
      .catch(() => setTarix([]));
  };

  const toggleActive = async (user) => {
    setError('');
    setSuccess('');
    const activating = !user.is_active;

    if (!activating && !window.confirm(
      tr("\"{full_name}\" faolsizlantirilsinmi?\n\nU ilovaga kira olmaydi, lekin test natijalari va nishonlari saqlanib qoladi.", { full_name: user.full_name })
    )) return;

    try {
      await axios.put(`${API}/users/${user.id}`, { is_active: activating }, {
        headers: { authorization: token }
      });
      setSuccess(activating
        ? tr('"{ism}" qayta faollashtirildi.', { ism: user.full_name })
        : tr('"{ism}" faolsizlantirildi.', { ism: user.full_name }));
      loadUsers();
    } catch (err) {
      if (err.response?.data?.almashtirish_kerak) {
        openAlmashtirish(user, true);
        return;
      }
      setError(err.response?.data?.error || tr("Amal bajarilmadi."));
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '24px', fontSize: '22px' }}>{tr("Foydalanuvchilar")}</h2>

      <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>{tr("Yangi xodim qo'shish")}</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <input
            type="text"
            placeholder={tr("Ism Familya *")}
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder={tr("Login (bo'sh qoldirsangiz o'zi beriladi)")}
            value={badgeNumber}
            onChange={e => setBadgeNumber(e.target.value)}
            style={inputStyle}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder={tr("Parol * (kamida 6 belgi)")}
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            />
            <button
              type="button"
              onClick={() => { setPassword(randomPassword()); setError(''); setSuccess(''); }}
              title={tr("Tasodifiy parol yaratish")}
              style={genBtnStyle}
            >
              🎲 {tr("Yaratish")}
            </button>
          </div>
          <select value={role} onChange={e => setRole(e.target.value)} style={inputStyle}>
            {ROLES.map(r => <option key={r.value} value={r.value}>{tr("Rol: {label}", { label: r.label })}</option>)}
          </select>
          <select
            value={staffType}
            onChange={e => setStaffType(e.target.value)}
            style={{ ...inputStyle, gridColumn: '1 / -1' }}
          >
            <option value="">👤 {tr("Lavozimi: tanlanmagan (kunlik topshiriq ko'rinmaydi)")}</option>
            {STAFF_TYPES.map(s => (
              <option key={s.value} value={s.value}>👤 {s.label}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder={tr("Darajasi (masalan: Kapitan)")}
            value={rank}
            onChange={e => setRank(e.target.value)}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder={tr("Bo'limi")}
            value={department}
            onChange={e => setDepartment(e.target.value)}
            style={inputStyle}
          />
          <select
            value={workZoneId}
            onChange={e => setWorkZoneId(e.target.value)}
            style={{ ...inputStyle, gridColumn: '1 / -1' }}
          >
            <option value="">📍 {tr("Ish joyi: biriktirilmagan")}</option>
            {zones.map(z => (
              <option key={z.id} value={z.id}>📍 {tr("Ish joyi: {name}", { name: z.name })}</option>
            ))}
          </select>

          <select
            value={districtId}
            onChange={e => setDistrictId(e.target.value)}
            style={{ ...inputStyle, gridColumn: '1 / -1' }}
          >
            <option value="">🏙️ {tr("Tuman: biriktirilmagan (yig'ilishga kira olmaydi)")}</option>
            {districts.map(d => (
              <option key={d.id} value={d.id}>🏙️ {tr("Tuman: {name}", { name: d.name })}</option>
            ))}
          </select>

          {/* Probatsiya xodimi faqat o'z tumani mahkumlarini ko'radi —
              tumansiz u reyestrga umuman kira olmaydi. */}
          {role === 'probatsiya' && !districtId && (
            <div style={{ gridColumn: '1 / -1', background: '#fff8e6', color: '#8a6d2d', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' }}>
              {tr("Probatsiya xodimiga tuman biriktirish shart — u faqat o'z tumani mahkumlarini ko'radi. Tumansiz reyestr ochilmaydi.")}
            </div>
          )}

          {error && (
            <div style={{ gridColumn: '1 / -1', background: '#ffeaea', color: '#c93636', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ gridColumn: '1 / -1', background: '#e9f9ed', color: '#2d8a2d', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' }}>
              {success}
            </div>
          )}

          <button
            onClick={createUser}
            disabled={saving}
            style={{
              padding: '10px 24px',
              background: saving ? '#888' : '#1a1a2e',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              gridColumn: '1 / -1',
              cursor: saving ? 'default' : 'pointer'
            }}
          >
            {saving ? tr("Qo'shilmoqda...") : tr("Qo'shish")}
          </button>
        </div>

        <p style={{ marginTop: '12px', marginBottom: 0, color: '#999', fontSize: '12px' }}>
          {tr("Xodim mobil ilovaga shu login va parol bilan kiradi. Loginni o'zingiz o'ylab topmoqchi bo'lmasangiz bo'sh qoldiring — tizim keyingi bo'sh raqamni o'zi beradi va qo'shilgandan keyin sizga ko'rsatadi. Keyinroq tahrirlash orqali boshqasiga almashtirsangiz bo'ladi.")}
        </p>
      </div>

      <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Ism")}</th>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Login")}</th>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Daraja")}</th>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Bo'lim")}</th>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Ish joyi")}</th>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Rol")}</th>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Holat")}</th>
              <th style={{ textAlign: 'right', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Ball")}</th>
              <th style={{ textAlign: 'right', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Amallar")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid #f9f9f9', opacity: user.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '10px 8px', fontWeight: '500' }}>
                  {user.full_name}
                  {user.id === myId && (
                    <span style={{ marginLeft: '6px', color: '#aaa', fontSize: '12px' }}>{tr("(siz)")}</span>
                  )}
                </td>
                <td style={{ padding: '10px 8px', color: '#888' }}>{user.badge_number}</td>
                <td style={{ padding: '10px 8px', color: '#888' }}>{user.rank}</td>
                <td style={{ padding: '10px 8px', color: '#888' }}>
                  {user.department}
                  {staffTypeLabel(user.staff_type)
                    ? <div style={{ color: '#4f8ef7', fontSize: '12px', marginTop: '2px' }}>
                        👤 {staffTypeLabel(user.staff_type)}
                      </div>
                    : user.role === 'user' && (
                        <div style={{ color: '#c47f17', fontSize: '12px', marginTop: '2px' }}>
                          👤 {tr("lavozimi belgilanmagan")}
                        </div>
                      )}
                </td>
                <td style={{ padding: '10px 8px' }}>
                  {user.district_name && (
                    <div style={{ color: '#555', fontSize: '13px', marginBottom: '2px' }}>
                      🏙️ {user.district_name}
                    </div>
                  )}
                  {user.work_zone_name ? (
                    <span style={{ color: '#555', fontSize: '13px' }}>
                      📍 {user.work_zone_name}
                    </span>
                  ) : (
                    <span style={{ color: '#c93636', fontSize: '12px' }}>
                      {tr("biriktirilmagan")}
                    </span>
                  )}
                </td>
                <td style={{ padding: '10px 8px' }}>
                  <span style={{
                    background: user.role === 'admin' ? '#fff0e6' : '#e6f0ff',
                    color: user.role === 'admin' ? '#f7924f' : '#4f8ef7',
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '12px'
                  }}>
                    {user.role}
                  </span>
                </td>
                <td style={{ padding: '10px 8px' }}>
                  <span style={{
                    background: user.is_active ? '#e9f9ed' : '#f0f0f0',
                    color: user.is_active ? '#2d8a2d' : '#999',
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '12px'
                  }}>
                    {user.is_active ? tr("Faol") : tr("Faol emas")}
                  </span>
                </td>
                <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#4f8ef7' }}>{user.score}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => openEdit(user)}
                    style={{ ...genBtnStyle, padding: '6px 12px', fontSize: '12px', marginRight: '6px' }}
                  >
                    {tr("Tahrirlash")}
                  </button>
                  {user.is_active && user.district_id && (
                    <button
                      onClick={() => openAlmashtirish(user)}
                      title={tr("Mahkumlari va rejadagi sayyor sudlarini boshqa inspektorga o'tkazish")}
                      style={{ ...genBtnStyle, padding: '6px 12px', fontSize: '12px', marginRight: '6px' }}
                    >
                      🔁 {tr("Almashtirish")}
                    </button>
                  )}
                  <button
                    onClick={() => toggleActive(user)}
                    disabled={user.id === myId}
                    title={user.id === myId ? tr("O'zingizni faolsizlantira olmaysiz") : ''}
                    style={{
                      ...genBtnStyle,
                      padding: '6px 12px',
                      fontSize: '12px',
                      color: user.id === myId ? '#bbb' : (user.is_active ? '#c93636' : '#2d8a2d'),
                      cursor: user.id === myId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {user.is_active ? tr("Faolsizlantirish") : tr("Faollashtirish")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>🔁 {tr("Xodim almashtirishlar tarixi")}</h3>
          <button
            onClick={() => (tarix ? setTarix(null) : loadTarix())}
            style={{ ...genBtnStyle, padding: '6px 12px', fontSize: '12px' }}
          >
            {tarix ? tr("Yopish") : tr("Ko'rsatish")}
          </button>
        </div>
        {tarix && (tarix.length === 0 ? (
          <p style={{ color: '#999', fontSize: '13px', marginBottom: 0 }}>{tr("Hali almashtirish bo'lmagan")}</p>
        ) : (
          <div style={{ overflowX: 'auto', marginTop: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0', color: '#888', textAlign: 'left' }}>
                  <th style={{ padding: '8px', fontWeight: '500' }}>{tr("Sana")}</th>
                  <th style={{ padding: '8px', fontWeight: '500' }}>{tr("Tuman")}</th>
                  <th style={{ padding: '8px', fontWeight: '500' }}>{tr("Kimdan → kimga")}</th>
                  <th style={{ padding: '8px', fontWeight: '500' }}>{tr("O'tkazildi")}</th>
                  <th style={{ padding: '8px', fontWeight: '500' }}>{tr("Sabab")}</th>
                  <th style={{ padding: '8px', fontWeight: '500' }}>{tr("Kim bajardi")}</th>
                </tr>
              </thead>
              <tbody>
                {tarix.map(t => (
                  <tr key={t.id} style={{ borderBottom: '1px solid #f9f9f9', verticalAlign: 'top' }}>
                    <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{new Date(t.created_at).toLocaleString('uz-UZ')}</td>
                    <td style={{ padding: '8px' }}>{t.tuman_nomi || '—'}</td>
                    <td style={{ padding: '8px' }}>
                      {t.eski_xodim_nomi} → <b>{t.yangi_xodim_nomi}</b>
                      {t.eski_xodim_faolsizlantirildi && (
                        <div style={{ color: '#c93636', fontSize: '12px' }}>{tr("eski xodim faolsizlantirildi")}</div>
                      )}
                    </td>
                    <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                      {tr("{mahkum} ta mahkum, {sud} ta sayyor sud.", { mahkum: t.mahkum_soni, sud: t.sayyor_sud_soni })}
                    </td>
                    <td style={{ padding: '8px' }}>{t.sabab}</td>
                    <td style={{ padding: '8px', color: '#888' }}>{t.admin_nomi || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {alm && (
        <div
          onClick={() => setAlm(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 100
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-label={tr("Xodimni almashtirish")}
            style={{
              background: '#fff', borderRadius: '12px', padding: '24px',
              width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '6px', fontSize: '18px' }}>
              🔁 {tr("Xodimni almashtirish")}
            </h3>
            <p style={{ marginTop: 0, color: '#666', fontSize: '13px' }}>
              {alm.xodim.full_name}{alm.xodim.tuman_nomi ? ` · ${alm.xodim.tuman_nomi}` : ''}
            </p>

            <div style={{ background: '#f5f7fb', borderRadius: '8px', padding: '12px', fontSize: '14px', marginBottom: '14px' }}>
              {tr("Yangi inspektorga o'tadi:")}
              <div style={{ marginTop: '6px', fontWeight: '600' }}>
                {tr("{mahkum} ta mahkum, {sud} ta sayyor sud.", { mahkum: alm.mahkum_soni, sud: alm.sayyor_sud_soni })}
              </div>
              <div style={{ marginTop: '6px', color: '#777', fontSize: '12px' }}>
                {tr("Kim qachon mas'ul bo'lgani o'chirilmaydi. O'tkazilib bo'lgan sayyor sudlar o'zgarmaydi.")}
              </div>
            </div>

            {alm.nomzodlar.length === 0 ? (
              <div style={{ background: '#fff8e6', color: '#8a6d2d', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' }}>
                {tr("Bu tumanda boshqa faol probatsiya xodimi yo'q. Avval yangi xodimni qo'shing.")}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                <select
                  value={alm.yangiId}
                  onChange={e => setAlm({ ...alm, yangiId: e.target.value })}
                  style={inputStyle}
                  aria-label={tr("Yangi inspektor")}
                >
                  <option value="">{tr("Yangi inspektorni tanlang")}</option>
                  {alm.nomzodlar.map(n => (
                    <option key={n.id} value={n.id}>
                      {n.full_name} — {tr("{soni} ta mahkum", { soni: n.mahkum_soni })}
                    </option>
                  ))}
                </select>
                <textarea
                  rows={3}
                  placeholder={tr("Sabab * (masalan: boshqa ishga o'tdi, buyruq №…)")}
                  value={alm.sabab}
                  onChange={e => setAlm({ ...alm, sabab: e.target.value })}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
                <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '14px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={alm.faolsizlantirish}
                    disabled={alm.xodim.id === myId}
                    onChange={e => setAlm({ ...alm, faolsizlantirish: e.target.checked })}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    {tr("Eski xodimni faolsizlantirish")}
                    <span style={{ display: 'block', color: '#888', fontSize: '12px' }}>
                      {tr("Ishdan ketgan bo'lsa belgilang: tizimga kira olmaydi, telefoniga bildirishnoma bormaydi.")}
                    </span>
                  </span>
                </label>
              </div>
            )}

            {almError && (
              <div style={{ marginTop: '12px', background: '#ffeaea', color: '#c93636', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' }}>
                {almError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setAlm(null)} style={{ ...genBtnStyle, padding: '10px 20px' }}>
                {tr("Bekor qilish")}
              </button>
              {alm.nomzodlar.length > 0 && (
                <button
                  onClick={saveAlmashtirish}
                  disabled={almSaving}
                  style={{
                    padding: '10px 24px',
                    background: almSaving ? '#888' : '#1a1a2e',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: almSaving ? 'default' : 'pointer'
                  }}
                >
                  {almSaving ? tr("Saqlanmoqda...") : tr("Almashtirish")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div
          onClick={() => setEditing(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 100
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '12px', padding: '24px',
              width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px' }}>
              {tr("Xodimni tahrirlash")}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <input
                type="text"
                placeholder={tr("Ism Familya")}
                value={editing.full_name || ''}
                onChange={e => setEditing({ ...editing, full_name: e.target.value })}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder={tr("Login")}
                title={tr("Xodim ilovaga shu login bilan kiradi. Bo'sh qoldirsangiz o'zgarmaydi.")}
                value={editing.badge_number || ''}
                onChange={e => setEditing({ ...editing, badge_number: e.target.value })}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder={tr("Darajasi")}
                value={editing.rank || ''}
                onChange={e => setEditing({ ...editing, rank: e.target.value })}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder={tr("Bo'limi")}
                value={editing.department || ''}
                onChange={e => setEditing({ ...editing, department: e.target.value })}
                style={inputStyle}
              />
              <select
                value={editing.role}
                onChange={e => setEditing({ ...editing, role: e.target.value })}
                disabled={editing.id === myId}
                title={editing.id === myId ? tr("O'z rolingizni o'zgartira olmaysiz") : ''}
                style={{ ...inputStyle, background: editing.id === myId ? '#f5f5f5' : '#fff' }}
              >
                {ROLES.map(r => <option key={r.value} value={r.value}>{tr("Rol: {label}", { label: r.label })}</option>)}
              </select>
              <input
                type="number"
                min="0"
                placeholder={tr("Ball")}
                value={editing.score}
                onChange={e => setEditing({ ...editing, score: e.target.value })}
                style={inputStyle}
              />
              <select
                value={editing.staff_type || ''}
                onChange={e => setEditing({ ...editing, staff_type: e.target.value })}
                style={{ ...inputStyle, gridColumn: '1 / -1' }}
              >
                <option value="">👤 {tr("Lavozimi: tanlanmagan (kunlik topshiriq ko'rinmaydi)")}</option>
                {STAFF_TYPES.map(s => (
                  <option key={s.value} value={s.value}>👤 {s.label}</option>
                ))}
              </select>
              <select
                value={editing.work_zone_id || ''}
                onChange={e => setEditing({ ...editing, work_zone_id: e.target.value })}
                style={{ ...inputStyle, gridColumn: '1 / -1' }}
              >
                <option value="">📍 {tr("Ish joyi: biriktirilmagan")}</option>
                {zones.map(z => (
                  <option key={z.id} value={z.id}>📍 {tr("Ish joyi: {name}", { name: z.name })}</option>
                ))}
              </select>

              <select
                value={editing.district_id || ''}
                onChange={e => setEditing({ ...editing, district_id: e.target.value })}
                style={{ ...inputStyle, gridColumn: '1 / -1' }}
              >
                <option value="">🏙️ {tr("Tuman: biriktirilmagan (yig'ilishga kira olmaydi)")}</option>
                {districts.map(d => (
                  <option key={d.id} value={d.id}>🏙️ {tr("Tuman: {name}", { name: d.name })}</option>
                ))}
              </select>

              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder={tr("Yangi parol (bo'sh qoldirsangiz o'zgarmaydi)")}
                  value={editing.newPassword}
                  onChange={e => setEditing({ ...editing, newPassword: e.target.value })}
                  style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                />
                <button
                  type="button"
                  onClick={() => setEditing({ ...editing, newPassword: randomPassword() })}
                  title={tr("Tasodifiy parol yaratish")}
                  style={genBtnStyle}
                >
                  🎲 {tr("Yaratish")}
                </button>
              </div>

              {editError && (
                <div style={{ gridColumn: '1 / -1', background: '#ffeaea', color: '#c93636', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' }}>
                  {editError}
                  {editKerak && (
                    <button
                      type="button"
                      onClick={() => { const u = editKerak; setEditing(null); setEditKerak(null); openAlmashtirish(u); }}
                      style={{ ...genBtnStyle, display: 'block', marginTop: '8px', padding: '6px 12px', fontSize: '12px' }}
                    >
                      🔁 {tr("Mahkumlarni boshqa inspektorga o'tkazish")}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditing(null)}
                style={{ ...genBtnStyle, padding: '10px 20px' }}
              >
                {tr("Bekor qilish")}
              </button>
              <button
                onClick={saveEdit}
                disabled={editSaving}
                style={{
                  padding: '10px 24px',
                  background: editSaving ? '#888' : '#1a1a2e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: editSaving ? 'default' : 'pointer'
                }}
              >
                {editSaving ? tr("Saqlanmoqda...") : tr("Saqlash")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
